import { useRouter } from 'next/router';
import { Separator } from '@global-ecom/nitro-uds/elements';

import { useFeature } from 'hooks/useFeature';
import { usePdp } from 'hooks/usePdp';
import { useTranslate } from 'hooks/useTranslations';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { CallToActions } from 'ui/content/CallToActions';
import { getSizesFromSizeGroups } from 'utils/products';
import { SignUpFormPdpComingSoon } from 'ui/content/SignUpFormPdpComingSoon';
import { StockMessage } from 'ui/components/Sizes/StockMessage';
import { StockMessageSFCC } from 'ui/components/Sizes/StockMessageSFCC';
import { Link } from 'ui/elements/Link';
import {
  ProductColorPicker,
  ProductCustomizer,
  ProductMessages,
  ProductSizePicker,
  ProductFitOptions,
} from 'ui/pages/ProductDetailsPages';
import { buildProductUrl } from 'utils/buildProductUrl';
import { SizeGroup } from '__generated__/graphql';
import { isAppExclusiveValidRange } from 'utils/isAppExclusiveValidRange';

import { SoldOutBanner } from './SoldOutBanner';
import { ModelMeasurementCallout } from './ModelMeasurementCallout';

// Shows Products color picker, product messages (such as coming soon, promos,
// etc), product customizer button, and product size picker.
export const ProductVariantSelectors = ({
  inventory,
}: {
  inventory?: number;
}) => {
  const router = useRouter();
  const t = useTranslate();
  const {
    desiredSize,
    productIsOrderable,
    masterProduct,
    variation,
    content,
    isAppOnly,
  } = usePdp();

  const {
    staticFeatures: { useStockInfoRedesign, multipleModelMeasurementEnabled },
  } = useSiteConfig();

  const personalizationEnabled = useFeature('PERSONALIZATION_ENABLED');
  const showStockMessageBySFCCLogic = useFeature(
    'PDP_SHOW_DETAILED_STOCK_MESSAGES'
  );

  const enableStockMessage = useFeature('PDP_ENABLE_STOCK_MESSAGE');

  const productName = variation?.name || masterProduct?.name || '';
  const exploreMoreCTA = content?.ctaAboveSpecialMessage;
  let queryParams = '';
  if (router.query?.swatch) {
    queryParams = `?swatch=${router.query.swatch}`;
  }

  if (masterProduct?.displayOutOfStock?.soldoutWithRecommender) {
    return <SoldOutBanner />;
  }

  const sizes = getSizesFromSizeGroups(
    variation?.sizeGroups ?? ([] as SizeGroup[])
  );

  const selectedSize = desiredSize
    ? sizes?.find(x => x.id === desiredSize)
    : undefined;

  const showSelectFitLabel = !!(
    variation?.sizeGroups?.[0]?.label || masterProduct?.fitOptions
  );

  // Logic for where to show Stock Message component
  const hasModelText = !!variation?.modelMeasurementText;
  const showNextToSelectFitLabel = showSelectFitLabel && !hasModelText;
  const showNextToModelText = hasModelText;
  const showAlone = !showSelectFitLabel && !hasModelText;

  const stockMessage = !showStockMessageBySFCCLogic ? (
    <StockMessage
      className="absolute right-0 top-4"
      selectedSize={selectedSize}
      quantity={inventory}
    />
  ) : null;

  const stockMessageSFFC = enableStockMessage ? (
    <div className="mb-4">
      <StockMessageSFCC selectedSize={selectedSize} quantity={inventory} />
    </div>
  ) : null;

  const stockMessageOrSFFC = stockMessage ?? stockMessageSFFC;

  return (
    <>
      <ProductColorPicker />
      {variation?.modelMeasurementText && multipleModelMeasurementEnabled && (
        <ModelMeasurementCallout
          measurementText={variation.modelMeasurementText}
        />
      )}
      {exploreMoreCTA && (
        <CallToActions
          ctas={exploreMoreCTA}
          className="font-bold border border-gray-600 rounded-sm uppercase py-4"
        />
      )}
      {productIsOrderable &&
        variation?.configureID?.enabled &&
        personalizationEnabled &&
        masterProduct && (
          <>
            <label className="text-xs font-bold">{t('personalization')}</label>
            <Link
              as="button"
              href={`${buildProductUrl(
                masterProduct?.id,
                productName
              )}/personalise${queryParams}`}
              className="flex-none flex flex-col relative"
            >
              {t('personalise')}
            </Link>
          </>
        )}
      <Separator gap="none" />
      <ProductMessages />
      {variation && productIsOrderable ? (
        <>
          {!isAppExclusiveValidRange(
            isAppOnly,
            variation?.appOnlyDateTimeFrom,
            variation?.appOnlyDateTimeTo
          ) && (
            <>
              <ProductCustomizer />
              <div className="relative">
                <div className="flex">
                  <h2 id="fit-options-title" className="font-bold text-xl mb-4">
                    {!showSelectFitLabel ? t('sizePdp') : t('selectFit')}
                  </h2>
                  {showSelectFitLabel &&
                    showNextToSelectFitLabel &&
                    stockMessage}
                </div>
                <div
                  className="flex justify-between"
                  data-test-id="model-inventory"
                >
                  <>
                    {variation?.modelMeasurementText &&
                      !multipleModelMeasurementEnabled && (
                        <p
                          className={`text-xs font-bold uppercase mr-2 mb-4${
                            useStockInfoRedesign ? ' place-self-center' : ''
                          }`}
                          data-test-id="model-measurement-text"
                        >
                          {variation.modelMeasurementText}
                        </p>
                      )}
                    {(showNextToModelText || showAlone) &&
                      (useStockInfoRedesign
                        ? stockMessageOrSFFC
                        : stockMessage)}
                  </>
                </div>
                <ProductFitOptions />
                {useStockInfoRedesign ? (
                  <ProductSizePicker />
                ) : (
                  <ProductSizePicker inventory={inventory} />
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <SoldOutBanner message={variation?.displayOutOfStock?.displayValue} />
          <SignUpFormPdpComingSoon
            signUpForm={content?.comingSoonSignUpForm}
            variation={variation}
          />
        </>
      )}
    </>
  );
};
